import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckSquare, Plus, ExternalLink, Clock, User, Tag, Building2 } from "lucide-react";
import { format } from "date-fns";
import { CreateTaskDialog } from "@/components/CreateTaskDialog";

const priorityColors: Record<string, string> = {
  urgent: "bg-red-500 text-white",
  high: "bg-orange-500 text-white",
  medium: "bg-yellow-500 text-black",
  low: "bg-green-500 text-white"
};

const statusColors: Record<string, string> = {
  open: "bg-blue-100 text-blue-800 dark:bg-blue-950/20 dark:text-blue-200",
  in_progress: "bg-purple-100 text-purple-800 dark:bg-purple-950/20 dark:text-purple-200",
  pending_info: "bg-yellow-100 text-yellow-800 dark:bg-yellow-950/20 dark:text-yellow-200",
  resolved: "bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-200",
  closed: "bg-gray-100 text-gray-800 dark:bg-gray-950/20 dark:text-gray-200"
};

type TasksListResponse = {
  tasks: any[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalTasks: number;
  };
};

export function TasksView({ onClose }: { onClose: () => void }) {
  const [, setLocation] = useLocation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const { data: ticketsData, isLoading } = useQuery<TasksListResponse>({
    queryKey: ["tickets-slideout", { limit: 5 }],
    queryFn: async () => {
      const url = new URL("/api/tasks", window.location.origin);
      url.searchParams.set("limit", "5");
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
  });

  const tickets = ticketsData?.tasks || [];
  const activeTickets = tickets.filter(t => !["resolved", "closed"].includes(t.status)).slice(0, 5);

  const handleViewAllTasks = () => {
    onClose();
    setLocation("/tasks");
  };

  const handleOpenTicket = (ticketId: number) => {
    onClose();
    setLocation(`/tasks?open=${ticketId}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Recent Tasks</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-start space-x-3">
                  <div className="h-4 w-16 bg-gray-200 rounded animate-pulse mt-1" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 bg-gray-200 rounded w-2/3 animate-pulse" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Recent Tasks</h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create
          </Button>
          <Button variant="outline" size="sm" onClick={handleViewAllTasks}>
            <ExternalLink className="h-4 w-4 mr-2" />
            View All
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {activeTickets.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <CheckSquare className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No active tasks</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setIsCreateDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Task
              </Button>
            </CardContent>
          </Card>
        ) : (
          activeTickets.map((ticket) => (
            <Card
              key={ticket.id}
              className="hover:shadow-sm transition-shadow cursor-pointer"
              onClick={() => handleOpenTicket(ticket.id)}
            >
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-muted-foreground">
                      {ticket.ticketNumber}
                    </span>
                    <Badge className={`text-xs ${priorityColors[ticket.priority] || priorityColors.medium}`}>
                      {String(ticket.priority).toUpperCase()}
                    </Badge>
                    <Badge className={`text-xs ${statusColors[ticket.status] || statusColors.open}`}>
                      {String(ticket.status).replace("_", " ").toUpperCase()}
                    </Badge>
                  </div>

                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-1">
                    {ticket.title}
                  </p>

                  {ticket.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                      {ticket.description}
                    </p>
                  )}

                  <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                    {ticket.clientName && (
                      <div className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {ticket.clientName}
                      </div>
                    )}
                    {ticket.assignedToName && (
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {ticket.assignedToName}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {format(new Date(ticket.createdAt), "MMM d")}
                    </div>
                    {ticket.tags && ticket.tags.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        {ticket.tags.slice(0, 2).join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {activeTickets.length > 0 && (
        <div className="pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center"
            onClick={handleViewAllTasks}
          >
            View All Tasks ({ticketsData?.pagination?.totalTasks || tickets.length})
          </Button>
        </div>
      )}

      <CreateTaskDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />
    </div>
  );
}
